/**
 * ScriptLibraryTab.jsx
 *
 * Settings tab for managing the custom remediation script library
 * (GET/POST/PUT/DELETE /api/EndpointHealth/scripts).
 *
 * IT staff can author, store, version, edit and delete the actual PowerShell / Bash
 * payloads that RunScript wizard steps dispatch. System scripts may be edited (which
 * bumps their version) but not deleted. Editing a body increments Version server-side.
 *
 * Follows the Service Provider Contracts settings look & feel: left-aligned table
 * headers/content and red (#f63040) accents for interactive elements.
 *
 * @module SettingsPages/components/remediation/ScriptLibraryTab
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
    FaCode, FaPlus, FaEdit, FaTrashAlt, FaSpinner, FaTimes, FaSave,
    FaExclamationTriangle, FaLock
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';
import { REMEDIATION_CATEGORIES, RISK_LEVELS, SCRIPT_PLATFORMS } from './remediationActionMeta';
import './RemediationConfig.scss';

const emptyScript = () => ({
    scriptKey: '',
    name: '',
    description: '',
    category: 'Storage',
    platform: 'PowerShell',
    scriptBody: '',
    parametersJson: '',
    riskLevel: 'Low',
    requiresElevation: false,
    estimatedDurationSeconds: 30,
    isActive: true
});

/** Derive a kebab-case script key suggestion from a display name. */
const slugify = (name) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 150);

const ScriptLibraryTab = ({ apiFetch }) => {
    const [scripts, setScripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyScript());
    const [keyTouched, setKeyTouched] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const fetchScripts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/EndpointHealth/scripts');
            if (res.ok) setScripts(await res.json());
            else toast.error('Failed to load scripts');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load script library');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { fetchScripts(); }, [fetchScripts]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyScript());
        setKeyTouched(false);
        setShowModal(true);
    };

    const openEdit = (s) => {
        setEditingId(s.remediationScriptId);
        setForm({
            scriptKey: s.scriptKey ?? '',
            name: s.name ?? '',
            description: s.description ?? '',
            category: s.category ?? 'Storage',
            platform: s.platform ?? 'PowerShell',
            scriptBody: s.scriptBody ?? '',
            parametersJson: s.parametersJson ?? '',
            riskLevel: s.riskLevel ?? 'Low',
            requiresElevation: s.requiresElevation ?? false,
            estimatedDurationSeconds: s.estimatedDurationSeconds ?? 30,
            isActive: s.isActive ?? true,
            isSystemScript: s.isSystemScript ?? false,
            version: s.version
        });
        setKeyTouched(true);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyScript());
        setKeyTouched(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => {
            const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
            // Auto-suggest a script key from the name until the user edits the key directly.
            if (name === 'name' && !editingId && !keyTouched) {
                next.scriptKey = slugify(value);
            }
            return next;
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('Script name is required'); return; }
        if (!editingId && !form.scriptKey.trim()) { toast.error('Script key is required'); return; }
        if (!form.scriptBody.trim()) { toast.error('Script body is required'); return; }

        setSaving(true);
        try {
            const payload = {
                ...form,
                estimatedDurationSeconds: parseInt(form.estimatedDurationSeconds, 10) || 30,
                description: form.description || null,
                parametersJson: form.parametersJson?.trim() ? form.parametersJson : null
            };
            // ScriptKey is immutable on update — omit it from the PUT body.
            if (editingId) delete payload.scriptKey;

            const url = editingId
                ? `/api/EndpointHealth/scripts/${editingId}`
                : '/api/EndpointHealth/scripts';
            const method = editingId ? 'PUT' : 'POST';
            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success(editingId ? 'Script updated' : 'Script created');
                closeModal();
                fetchScripts();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.title ?? err.message ?? (res.status === 409 ? 'A script with that key already exists' : 'Failed to save script'));
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await apiFetch(`/api/EndpointHealth/scripts/${id}`, { method: 'DELETE' });
            if (res.ok) { toast.success('Script deleted'); setDeleteConfirmId(null); fetchScripts(); }
            else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.title ?? err.message ?? 'Failed to delete script');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        }
    };

    if (loading) return <div className="module-template-config loading-state"><FaSpinner className="spin" /> Loading scripts&hellip;</div>;

    return (
        <div className="module-template-config script-library">
            <div className="config-header">
                <div className="config-title">
                    <FaCode className="config-icon" />
                    <div>
                        <h2>Script Library</h2>
                        <p>Author, version and reuse the PowerShell / Bash payloads that remediation wizard <strong>Run Script</strong> steps dispatch. Editing a script body increments its version.</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openCreate}><FaPlus /> New Script</button>
            </div>

            {scripts.length === 0 ? (
                <div className="empty-state">
                    <FaCode size={40} />
                    <p>No custom scripts yet.</p>
                    <p>Create reusable scripts here, then load them into wizard Run Script steps.</p>
                </div>
            ) : (
                <table className="config-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Key</th>
                            <th>Category</th>
                            <th>Platform</th>
                            <th>Risk</th>
                            <th>Version</th>
                            <th>Active</th>
                            <th style={{ width: 80 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scripts.map(s => (
                            <tr key={s.remediationScriptId} style={{ opacity: s.isActive ? 1 : 0.5 }}>
                                <td>
                                    <strong>{s.name}</strong>
                                    {s.isSystemScript && <span className="script-system-badge"><FaLock size={9} /> System</span>}
                                    {s.description && <div className="row-sub">{s.description}</div>}
                                </td>
                                <td><code style={{ fontSize: '0.76rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{s.scriptKey}</code></td>
                                <td>{s.category}</td>
                                <td>{s.platform}</td>
                                <td><span className={`script-risk risk-${s.riskLevel}`}>{s.riskLevel}</span></td>
                                <td><span className="script-version">v{s.version}</span></td>
                                <td>{s.isActive ? '✓' : '—'}</td>
                                <td className="actions-cell">
                                    <button className="btn-icon" title="Edit" onClick={() => openEdit(s)}><FaEdit /></button>
                                    {!s.isSystemScript && (
                                        deleteConfirmId === s.remediationScriptId ? (
                                            <>
                                                <button className="btn-icon btn-danger" title="Confirm delete" onClick={() => handleDelete(s.remediationScriptId)}><FaExclamationTriangle /></button>
                                                <button className="btn-icon" title="Cancel" onClick={() => setDeleteConfirmId(null)}><FaTimes /></button>
                                            </>
                                        ) : (
                                            <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteConfirmId(s.remediationScriptId)}><FaTrashAlt /></button>
                                        )
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-panel" style={{ maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Edit Script' : 'New Script'}{form.isSystemScript && <span className="script-system-badge"><FaLock size={9} /> System</span>}</h3>
                            <button className="btn-icon" onClick={closeModal}><FaTimes /></button>
                        </div>
                        <form className="modal-body" onSubmit={handleSave}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="scr-name">Name <span className="required">*</span></label>
                                    <input id="scr-name" name="name" type="text" className="form-control" value={form.name} onChange={handleChange} placeholder="e.g. Clear Temp Files" required />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="scr-key">Script Key <span className="required">*</span></label>
                                    <input id="scr-key" name="scriptKey" type="text" className="form-control"
                                        value={form.scriptKey}
                                        onChange={(e) => { setKeyTouched(true); handleChange(e); }}
                                        placeholder="e.g. clear-temp-files"
                                        disabled={!!editingId}
                                        required />
                                    <small className="field-help">{editingId ? 'Key is immutable once created.' : 'Stable lower-kebab-case key referenced by wizard steps.'}</small>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="scr-desc">Description</label>
                                <textarea id="scr-desc" name="description" className="form-control" rows={2} value={form.description} onChange={handleChange} placeholder="What does this script do and when should it be used?" />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="scr-cat">Category</label>
                                    <select id="scr-cat" name="category" className="form-control" value={form.category} onChange={handleChange}>
                                        {REMEDIATION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="scr-platform">Platform</label>
                                    <select id="scr-platform" name="platform" className="form-control" value={form.platform} onChange={handleChange}>
                                        {SCRIPT_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="scr-risk">Risk Level</label>
                                    <select id="scr-risk" name="riskLevel" className="form-control" value={form.riskLevel} onChange={handleChange}>
                                        {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="scr-body">Script Body <span className="required">*</span></label>
                                <textarea id="scr-body" name="scriptBody" className="form-control code-textarea" rows={12} value={form.scriptBody} onChange={handleChange} placeholder="# PowerShell / Bash script body" required />
                                <small className="field-help">The exact body dispatched to the device. Use {'{{Name}}'} placeholders for parameters.</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="scr-params">Parameters (JSON)</label>
                                <textarea id="scr-params" name="parametersJson" className="form-control code-textarea" rows={3} value={form.parametersJson} onChange={handleChange} placeholder='[{"name":"Path","displayName":"Folder","defaultValue":"C:\\Temp","type":"string","required":false}]' />
                                <small className="field-help">Optional. Describes customisable {'{{Name}}'} placeholders in the body.</small>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="scr-dur">Est. Duration (seconds)</label>
                                    <input id="scr-dur" name="estimatedDurationSeconds" type="number" min={1} className="form-control" value={form.estimatedDurationSeconds} onChange={handleChange} />
                                </div>
                                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input type="checkbox" name="requiresElevation" checked={form.requiresElevation} onChange={handleChange} />
                                        Requires elevation
                                    </label>
                                </div>
                                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                                        Active
                                    </label>
                                </div>
                            </div>

                            {editingId && form.version != null && (
                                <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0 0 8px' }}>
                                    Current version: v{form.version}. Saving a changed body will publish v{form.version + 1}.
                                </p>
                            )}

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? <><FaSpinner className="spin" /> Saving&hellip;</> : <><FaSave /> {editingId ? 'Update Script' : 'Create Script'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScriptLibraryTab;
